import { useEffect, useState } from 'react';
import creditService from '../../../../../shared/services/creditService';

const SUBSCRIBER_CREDITS_VERIFY_ERROR = 'Could not verify subscriber credits before calling.';
const SUBSCRIBER_CREDITS_READ_ERROR = 'Could not read subscriber credit balance.';

/**
 * Fetch another user's wallet credits (creator pre-call checks). Does not touch Redux wallet state.
 *
 * @param {string|null|undefined} subscriberId
 * @param {{ enabled?: boolean }} [options]
 */
export function useRemoteSubscriberWalletCredits(subscriberId, { enabled = true } = {}) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setCredits(null);
      setLoading(false);
      setError('');
      return undefined;
    }

    if (!subscriberId) {
      setCredits(null);
      setLoading(false);
      setError(SUBSCRIBER_CREDITS_VERIFY_ERROR);
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await creditService.getWalletBalance(subscriberId);
        if (cancelled) return;
        const balance = response?.data?.balance ?? response?.data?.data?.balance ?? response?.data;
        const parsedCredits = Number(balance?.credits);
        setCredits(Number.isFinite(parsedCredits) ? parsedCredits : null);
        if (!Number.isFinite(parsedCredits)) {
          setError(SUBSCRIBER_CREDITS_READ_ERROR);
        }
      } catch (err) {
        if (cancelled) return;
        setCredits(null);
        setError(err?.message || SUBSCRIBER_CREDITS_VERIFY_ERROR);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [subscriberId, enabled]);

  return { credits, loading, error };
}
